import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Navbar } from '@/components/landing/Navbar';
import { Footer } from '@/components/landing/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { toast } from 'sonner';
import {
  Briefcase,
  MapPin,
  Clock,
  ChevronDown,
  ChevronUp,
  Send,
  Sparkles,
  Heart,
  Rocket,
  Users,
  Coffee,
  Laptop,
  GraduationCap,
  CheckCircle2,
  Code,
  Server,
  ArrowRight,
} from 'lucide-react';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
};

const perks = [
  { icon: Rocket, title: 'High Impact Work', description: 'Build products used by thousands of businesses across India.' },
  { icon: GraduationCap, title: 'Learning Budget', description: 'Annual learning allowance for courses, conferences, and books.' },
  { icon: Coffee, title: 'Flexible Hours', description: 'We care about output, not hours. Work when you’re most productive.' },
  { icon: Laptop, title: 'Remote Friendly', description: 'Work from anywhere in India. We’re a distributed team.' },
  { icon: Heart, title: 'Health & Wellness', description: 'Comprehensive health insurance for you and your family.' },
  { icon: Users, title: 'Small Team, Big Impact', description: 'Every person matters. Your ideas shape the product directly.' },
];

const jobs = [
  {
    id: 'frontend-intern',
    icon: Code,
    title: 'Frontend Engineering Intern',
    team: 'Engineering',
    type: 'Internship • 6 months',
    location: 'Remote / Bengaluru',
    posted: 'January 2025',
    about: 'We’re looking for a passionate Frontend Engineering Intern to join our product team. You’ll work directly with our senior engineers to build beautiful, responsive interfaces for the Agentick platform. This is an incredible opportunity to gain hands-on experience with modern web technologies in a fast-paced startup environment.',
    responsibilities: [
      'Build and maintain UI components using React JS and Next JS',
      'Collaborate with designers to implement pixel-perfect, responsive layouts',
      'Write clean, maintainable code with proper component architecture',
      'Participate in code reviews and technical discussions',
      'Help improve frontend performance, accessibility, and user experience',
      'Contribute to our design system and component library',
    ],
    requirements: [
      'Currently pursuing or recently completed B.Tech/B.E. in Computer Science or equivalent',
      'Strong understanding of React JS fundamentals (hooks, state, props, lifecycle)',
      'Experience with Next JS is a strong plus',
      'Good knowledge of HTML5, CSS3, and Tailwind CSS',
      'Familiarity with Git and version control workflows',
      'Understanding of responsive design and cross-browser compatibility',
      'Enthusiasm to learn and grow in a startup environment',
    ],
    niceToHave: [
      'Experience with TypeScript',
      'Familiarity with Framer Motion or other animation libraries',
      'Knowledge of REST APIs and data fetching patterns',
      'Personal projects or open-source contributions to showcase',
    ],
    stipend: '₹15,000 – ₹25,000 / month (based on experience)',
  },
  {
    id: 'backend-engineer',
    icon: Server,
    title: 'Backend Engineer',
    team: 'Engineering',
    type: 'Full-time',
    location: 'Remote / Bengaluru',
    posted: 'January 2025',
    about: 'We’re hiring a Backend Engineer to build and scale the server-side infrastructure powering Agentick. You’ll design APIs, manage data pipelines, and ensure our AI chatbot platform runs reliably for thousands of businesses. If you love building robust, scalable systems and want to make a real impact at an early-stage startup, this is for you.',
    responsibilities: [
      'Design, develop, and maintain RESTful APIs using Java and Spring Boot',
      'Build scalable microservices architecture to support growing traffic',
      'Integrate with AI/ML services, WhatsApp APIs, and third-party platforms',
      'Optimize database queries and manage data storage (PostgreSQL, MongoDB)',
      'Implement authentication, authorization, and security best practices',
      'Write comprehensive unit and integration tests',
      'Participate in system design, architecture decisions, and code reviews',
    ],
    requirements: [
      '2–4 years of professional experience with Java and Spring Boot',
      'Strong understanding of REST API design principles and microservices',
      'Experience with relational databases (PostgreSQL/MySQL) and NoSQL (MongoDB)',
      'Proficiency in Git, CI/CD pipelines, and deployment workflows',
      'Understanding of security practices (OAuth2, JWT, encryption)',
      'Experience with cloud platforms (AWS, GCP, or Azure)',
      'Excellent problem-solving skills and attention to detail',
    ],
    niceToHave: [
      'Experience with message queues (Kafka, RabbitMQ)',
      'Familiarity with Docker and Kubernetes',
      'Knowledge of AI/ML integration or chatbot systems',
      'Contributions to open-source projects',
    ],
    stipend: 'Competitive salary + equity (based on experience)',
  },
];

function JobCard({ job }) {
  const [expanded, setExpanded] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', portfolio: '', coverLetter: '' });
  const [sending, setSending] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleApply = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.coverLetter) {
      toast.error('Please fill in all required fields.');
      return;
    }
    setSending(true);
    setTimeout(() => {
      const subject = `Application: ${job.title}`;
      const body = `Name: ${form.name}\nEmail: ${form.email}\nPhone: ${form.phone}\nPortfolio: ${form.portfolio}\n\nCover Letter:\n${form.coverLetter}`;
      const mailtoLink = `mailto:hello@agentick.in?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      window.open(mailtoLink, '_blank');
      toast.success('Opening your email client with application details...');
      setSending(false);
    }, 600);
  };

  return (
    <div className="glass-card rounded-2xl overflow-hidden transition-colors duration-500 hover:border-primary/15">
      {/* Header */}
      <div className="p-7">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center">
              <job.icon size={20} className="text-primary" />
            </div>
            <div>
              <h3 className="font-heading text-lg font-semibold text-foreground">{job.title}</h3>
              <p className="text-xs text-muted-foreground font-body">{job.team}</p>
            </div>
          </div>
          <Badge variant="premium" className="text-[10px]">{job.type}</Badge>
        </div>

        <div className="flex flex-wrap gap-4 text-sm text-muted-foreground font-body mb-4">
          <span className="flex items-center gap-1.5"><MapPin size={14} /> {job.location}</span>
          <span className="flex items-center gap-1.5"><Clock size={14} /> Posted {job.posted}</span>
          <span className="flex items-center gap-1.5"><Briefcase size={14} /> {job.stipend}</span>
        </div>

        <p className="text-sm text-muted-foreground font-body leading-relaxed mb-5">{job.about}</p>

        <div className="flex flex-wrap gap-3">
          <Button variant="premium" size="default" onClick={() => { setShowForm(!showForm); setExpanded(true); }}>
            Apply Now <ArrowRight size={14} />
          </Button>
          <button
            onClick={() => setExpanded(!expanded)}
            className="inline-flex items-center gap-1 text-sm text-primary font-medium hover:text-primary-glow transition-colors duration-200 bg-transparent border-none p-0 cursor-pointer font-body"
          >
            {expanded ? 'Hide details' : 'View details'}
            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
        </div>
      </div>

      {/* Expanded Details */}
      <motion.div
        initial={false}
        animate={{ height: expanded ? 'auto' : 0, opacity: expanded ? 1 : 0 }}
        transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
        className="overflow-hidden"
      >
        <div className="px-7 pb-7 border-t border-border/50 pt-6 space-y-6">
          {/* Responsibilities */}
          <div>
            <h4 className="font-heading text-sm font-semibold text-foreground mb-3">Responsibilities</h4>
            <ul className="space-y-2">
              {job.responsibilities.map((r) => (
                <li key={r} className="flex items-start gap-2.5 text-sm text-muted-foreground font-body">
                  <CheckCircle2 size={14} className="text-primary shrink-0 mt-0.5" />
                  {r}
                </li>
              ))}
            </ul>
          </div>

          {/* Requirements */}
          <div>
            <h4 className="font-heading text-sm font-semibold text-foreground mb-3">Requirements</h4>
            <ul className="space-y-2">
              {job.requirements.map((r) => (
                <li key={r} className="flex items-start gap-2.5 text-sm text-muted-foreground font-body">
                  <CheckCircle2 size={14} className="text-primary shrink-0 mt-0.5" />
                  {r}
                </li>
              ))}
            </ul>
          </div>

          {/* Nice to Have */}
          <div>
            <h4 className="font-heading text-sm font-semibold text-foreground mb-3">Nice to Have</h4>
            <ul className="space-y-2">
              {job.niceToHave.map((r) => (
                <li key={r} className="flex items-start gap-2.5 text-sm text-muted-foreground/70 font-body">
                  <Sparkles size={14} className="text-accent shrink-0 mt-0.5" />
                  {r}
                </li>
              ))}
            </ul>
          </div>

          {/* Application Form */}
          {showForm && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="border-t border-border/50 pt-6"
            >
              <h4 className="font-heading text-base font-semibold text-foreground mb-1">Apply for {job.title}</h4>
              <p className="text-xs text-muted-foreground font-body mb-5">Your application will be sent to hello@agentick.in</p>

              <form onSubmit={handleApply} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor={`${job.id}-name`} className="text-xs font-medium text-foreground font-body">Full Name *</Label>
                    <Input id={`${job.id}-name`} name="name" placeholder="Your full name" value={form.name} onChange={handleChange} className="bg-secondary/50 border-border/60 h-10 font-body text-sm" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor={`${job.id}-email`} className="text-xs font-medium text-foreground font-body">Email *</Label>
                    <Input id={`${job.id}-email`} name="email" type="email" placeholder="you@email.com" value={form.email} onChange={handleChange} className="bg-secondary/50 border-border/60 h-10 font-body text-sm" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor={`${job.id}-phone`} className="text-xs font-medium text-foreground font-body">Phone</Label>
                    <Input id={`${job.id}-phone`} name="phone" placeholder="+91 XXXXX XXXXX" value={form.phone} onChange={handleChange} className="bg-secondary/50 border-border/60 h-10 font-body text-sm" />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor={`${job.id}-portfolio`} className="text-xs font-medium text-foreground font-body">Portfolio / GitHub URL</Label>
                    <Input id={`${job.id}-portfolio`} name="portfolio" placeholder="https://github.com/you" value={form.portfolio} onChange={handleChange} className="bg-secondary/50 border-border/60 h-10 font-body text-sm" />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor={`${job.id}-cover`} className="text-xs font-medium text-foreground font-body">Cover Letter / Why Agentick? *</Label>
                  <Textarea id={`${job.id}-cover`} name="coverLetter" placeholder="Tell us why you want to join Agentick and what excites you about this role..." rows={4} value={form.coverLetter} onChange={handleChange} className="bg-secondary/50 border-border/60 font-body text-sm resize-none" />
                </div>
                <Button type="submit" variant="premium" size="lg" disabled={sending}>
                  {sending ? 'Preparing...' : 'Submit Application'}
                  <Send size={14} />
                </Button>
              </form>
            </motion.div>
          )}
        </div>
      </motion.div>
    </div>
  );
}

function PerksSection() {
  const [ref, isVisible] = useScrollAnimation(0.1);
  return (
    <section ref={ref} className="py-24 lg:py-32 relative">
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-accent/[0.03] blur-[100px]" />
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
          >
            <Badge variant="accent" className="mb-4">Why Join Us</Badge>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="font-heading text-3xl sm:text-4xl font-bold text-foreground mb-4 leading-tight"
          >
            Perks & Benefits
          </motion.h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {perks.map((perk, i) => (
            <motion.div
              key={perk.title}
              initial={{ opacity: 0, y: 25 }}
              animate={isVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.2 + i * 0.08 }}
              className="glass-card rounded-2xl p-6 hover:border-primary/15 transition-colors duration-500"
            >
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                <perk.icon size={18} className="text-primary" />
              </div>
              <h3 className="font-heading text-sm font-semibold text-foreground mb-1">{perk.title}</h3>
              <p className="text-xs text-muted-foreground font-body leading-relaxed">{perk.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function CareersPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-28 pb-16 lg:pt-36 lg:pb-20 overflow-hidden">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/4 w-[600px] h-[400px] rounded-full bg-primary/[0.05] blur-[130px]" />
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: `linear-gradient(hsl(210 20% 95% / 0.1) 1px, transparent 1px), linear-gradient(90deg, hsl(210 20% 95% / 0.1) 1px, transparent 1px)`,
          backgroundSize: '60px 60px',
        }} />

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeUp} transition={{ duration: 0.5 }}>
            <Badge variant="premium" className="mb-5">
              <Briefcase size={12} className="mr-1" />
              We’re Hiring
            </Badge>
          </motion.div>
          <motion.h1
            {...fadeUp}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-5 leading-tight"
          >
            Build the Future of{' '}
            <span className="gradient-text-primary">AI for SMBs</span>
          </motion.h1>
          <motion.p
            {...fadeUp}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-base lg:text-lg text-muted-foreground font-body leading-relaxed max-w-xl mx-auto"
          >
            Join a passionate team building the easiest WhatsApp AI platform for Indian businesses. We’re small, fast, and making a real difference.
          </motion.p>
        </div>
      </section>

      {/* Open Positions */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <motion.div {...fadeUp} transition={{ duration: 0.5, delay: 0.3 }} className="mb-8">
          <h2 className="font-heading text-2xl font-bold text-foreground mb-2">Open Positions</h2>
          <p className="text-sm text-muted-foreground font-body">{jobs.length} open roles • All applications go to hello@agentick.in</p>
        </motion.div>

        <div className="space-y-6">
          {jobs.map((job, i) => (
            <motion.div
              key={job.id}
              {...fadeUp}
              transition={{ duration: 0.5, delay: 0.4 + i * 0.15 }}
            >
              <JobCard job={job} />
            </motion.div>
          ))}
        </div>
      </section>

      <PerksSection />

      <Footer />
    </div>
  );
}
